import { waterCoolerAudio } from "./lib/water-cooler-audio";
import { useEffect, useRef, useState, type ReactNode, type FormEvent } from "react";
import type { ChatMessage } from "@serendiptv/contracts";
import { getStationChat, sendStationChat } from "./lib/api";

export function StationChat({ channel, stationName, available, children, volume, muted, enabled }: { channel: number; stationName: string; available: boolean; children: ReactNode; volume: number; muted: boolean; enabled: boolean }) {
  const [open, setOpen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const [bubbles, setBubbles] = useState(true);
  useEffect(() => {
    const update = () => waterCoolerAudio.configure(open && bubbles && enabled && !muted && document.visibilityState === "visible", volume);
    update();
    document.addEventListener("visibilitychange", update);
    return () => { document.removeEventListener("visibilitychange", update); waterCoolerAudio.configure(false, 0); };
  }, [open, bubbles, enabled, muted, volume]);
  useEffect(() => () => waterCoolerAudio.dispose(), []);
  useEffect(() => { if (!enabled) waterCoolerAudio.dispose(); }, [enabled]);
  return <section className={`station-chat ${open ? "station-chat-open" : ""}`} aria-label="Station chat" onKeyDown={(event) => {
    if (event.key === "Escape" && open) { event.stopPropagation(); setOpen(false); toggle.current?.focus(); }
  }}>
    <div className="chat-audience">{children}
    <button ref={toggle} className="chat-toggle" type="button" aria-label={open ? "Collapse station chat" : "Open station chat"} aria-expanded={open} aria-controls="station-chat-room" disabled={!available} onClick={() => { if (!open && !muted && volume > 0) waterCoolerAudio.unlock(); setOpen((value) => !value); }}>
      <span className="chat-toggle-face">CHAT <span aria-hidden="true">{open ? "−" : "+"}</span></span>
    </button></div>
    {open && enabled ? <ChatRoom key={channel} channel={channel} stationName={stationName} bubbles={bubbles} onBubbles={() => { if (!bubbles && !muted && volume > 0) waterCoolerAudio.unlock(); setBubbles((value) => !value); }} /> : <div className="speaker-grille" aria-hidden="true"><span /><span /><span /><span /><span /><span /><span /><span /></div>}
  </section>;
}

function ChatRoom({ channel, stationName, bubbles, onBubbles }: { channel: number; stationName: string; bubbles: boolean; onBubbles: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [author, setAuthor] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [connection, setConnection] = useState("Connecting…");
  const [sending, setSending] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const requestId = useRef<{ id: string; text: string } | null>(null);
  const postController = useRef<AbortController | null>(null);
  const refresh = useRef<() => void>(() => undefined);

  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    let controller: AbortController | null = null;
    const load = async () => {
      clearTimeout(timer);
      if (document.visibilityState !== "visible") return;
      controller?.abort();
      const current = new AbortController();
      controller = current;
      const timeout = setTimeout(() => current.abort(), 8_000);
      try {
        const room = await getStationChat(channel, current.signal);
        if (disposed || current.signal.aborted || room.channel !== channel) return;
        setAuthor(room.author);
        setMessages(room.messages);
        setConnection("");
      } catch {
        if (!disposed && controller === current) setConnection("Chat reconnecting…");
      } finally {
        clearTimeout(timeout);
        if (!disposed && controller === current) timer = setTimeout(() => void load(), 3_000);
      }
    };
    const visibility = () => {
      if (document.visibilityState === "visible") void load();
      else { clearTimeout(timer); controller?.abort(); }
    };
    refresh.current = () => void load();
    document.addEventListener("visibilitychange", visibility);
    void load();
    return () => {
      disposed = true;
      clearTimeout(timer);
      controller?.abort();
      postController.current?.abort();
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [channel]);

  useEffect(() => {
    if (stickToBottom.current && list.current) list.current.scrollTop = list.current.scrollHeight;
  }, [messages]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || sending || !author) return;
    // Retry the same id after a lost response so a delivered message is not duplicated.
    if (requestId.current?.text !== text) requestId.current = { id: crypto.randomUUID(), text };
    const controller = new AbortController();
    postController.current = controller;
    const timeout = setTimeout(() => controller.abort(), 8_000);
    setSending(true); setError("");
    try {
      await sendStationChat(channel, requestId.current.id, text, controller.signal);
      if (controller.signal.aborted) return;
      setDraft(""); requestId.current = null; stickToBottom.current = true;
      refresh.current();
    } catch (error) {
      setError(error instanceof Error && error.name !== "AbortError" ? error.message : "Message not confirmed. Try again.");
    } finally { clearTimeout(timeout); setSending(false); }
  };
  return <div id="station-chat-room" className="chat-room">
    <div className="chat-room-label cooler-heading">
      <span className="cooler-mini" aria-hidden="true"><i /><b /></span>
      <span className="cooler-name"><strong>WATER COOLER</strong><small>CH {channel} · {stationName}</small></span>
      <button className="cooler-sound" type="button" aria-label="Water cooler bubble sounds" aria-pressed={bubbles} title="Bubble sounds follow the TV volume" onClick={onBubbles}><span aria-hidden="true">◦◯</span><small>{bubbles ? "ON" : "OFF"}</small></button>
    </div>
    <div ref={list} className="chat-messages" role="log" aria-label={`Chat for ${stationName}`} aria-live="polite" aria-relevant="additions" tabIndex={0}
      onScroll={() => { if (list.current) stickToBottom.current = list.current.scrollHeight - list.current.scrollTop - list.current.clientHeight < 24; }}>
      {!messages.length ? <p className="chat-empty">{author ? "Quiet in here. Pull up a cup." : "Tuning in to chat…"}</p> : messages.map((message) => <p className="chat-message" key={message.id}>
        <span className="chat-author">{message.author}{message.author === author ? " (you)" : ""}</span>
        <span>{message.text}</span>
      </p>)}
    </div>
    <p className={`chat-status ${error || connection ? "" : "chat-status-note"}`} role="status">{error || connection || (author ? `${author} · Messages last 24h` : "")}</p>
    <form className="chat-compose" onSubmit={(event) => void submit(event)}>
      <input aria-label={`Message to ${stationName}`} placeholder="Around the cooler…" maxLength={280} value={draft} disabled={!author || sending} onChange={(event) => { setDraft(event.target.value); setError(""); }} autoComplete="off" />
      <button type="submit" disabled={!author || !draft.trim() || sending} aria-label="Send chat message">{sending ? "…" : "Send"}</button>
    </form>
  </div>;
}
